import { useState } from "react";
import { Code2, Save, Play, FileText, Sparkles } from "lucide-react";

const DEFAULT_SKILL_MD = `# My Custom Trading Strategy

## Description
Enter your strategy description here. The AI will interpret your rules and execute them.

## Markets
- 5min-btc-updown
- 15min-btc-updown
- 5min-eth-updown

## Entry Rules
- Buy YES when Binance BTC moves >0.3% up in 30s and PM price < fair prob
- Buy NO when Binance BTC moves >0.3% down in 30s and PM price > fair prob
- Only trade if edge > 15 cents

## Exit Rules
- Take profit at 90% of predicted probability
- Cut loss if price moves against by 20 cents
- Max hold time: 2 minutes

## Risk Management
- Max position: 5% of bankroll
- Min edge: 15c
- Stop trading after 3 consecutive losses
- Kelly fraction: 0.25 (quarter Kelly)
`;

export default function StrategyPage() {
  const [content, setContent] = useState(DEFAULT_SKILL_MD);
  const [aiPrompt, setAiPrompt] = useState("");
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const generateFromPrompt = () => {
    if (!aiPrompt.trim()) return;
    const generated = `# AI Generated Strategy

## Description
${aiPrompt}

## Markets
- 5min-btc-updown
- 5min-eth-updown

## Entry Rules
- Identified by AI based on user intent: "${aiPrompt}"
- Use latency arbitrage when edge > 10c
- Require volatility ATR > 20

## Exit Rules
- Profit target: 90% of predicted probability
- Stop loss: maximum 20c adverse move
- Time limit: 2 minutes from entry

## Risk Management
- Max position: 8% of bankroll
- Min edge: 10c
- Kelly fraction: 0.25
- Kill switch at 10% daily drawdown
`;
    setContent(generated);
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Code2 size={22} className="text-terminal-accent" />
          <div>
            <h1 className="text-xl font-bold text-white">STRATEGY EDITOR</h1>
            <p className="text-xs text-gray-500">Write your custom strategy in Markdown or describe it to AI</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className={`terminal-btn flex items-center gap-2 ${saved ? "border-green-500 text-green-400" : ""}`}
          >
            <Save size={14} />
            {saved ? "SAVED!" : "SAVE STRATEGY"}
          </button>
          <button className="terminal-btn-primary flex items-center gap-2">
            <Play size={14} />
            DEPLOY
          </button>
        </div>
      </div>

      {/* AI Prompt Generator */}
      <div className="terminal-window p-4">
        <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
          <Sparkles size={12} className="text-terminal-accent" />
          GENERATE WITH AI
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="Describe your strategy in plain English... e.g. 'Buy BTC up contracts when Binance spikes 0.3% in 30s'"
            className="terminal-input flex-1"
          />
          <button onClick={generateFromPrompt} className="terminal-btn-primary">
            <Sparkles size={14} />
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="terminal-window">
        <div className="terminal-header">
          <span className="flex items-center gap-2">
            <FileText size={12} />
            skill.md
          </span>
          <span className="text-gray-500">Markdown</span>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full h-96 bg-black/50 text-sm text-green-400 font-mono p-4 resize-none focus:outline-none border-none"
          spellCheck={false}
        />
      </div>

      {/* Preview */}
      <div className="terminal-window">
        <div className="terminal-header">
          <span className="flex items-center gap-2">
            <Code2 size={12} />
            PARSED RULES
          </span>
        </div>
        <div className="p-4 text-sm space-y-3">
          <div className="flex items-start gap-2">
            <span className="text-terminal-accent mt-0.5">&gt;</span>
            <div>
              <span className="text-gray-400">Markets:</span>
              <span className="text-white ml-2">5min-btc-updown, 15min-btc-updown, 5min-eth-updown</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-terminal-accent mt-0.5">&gt;</span>
            <div>
              <span className="text-gray-400">Entry:</span>
              <span className="text-white ml-2">Buy when edge {'>'} 15c</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-terminal-accent mt-0.5">&gt;</span>
            <div>
              <span className="text-gray-400">Risk:</span>
              <span className="text-white ml-2">Max position 5%, Quarter-Kelly 0.25</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
